import React, { useMemo, useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './NewDashboard.css';
import './MyClassicGptAgents.css';
import ColegioService from '../services/ColegioService';
import ParticularService from '../services/ParticularService';
import ActiveColegioBadge from './common/ActiveColegioBadge';
import SolicitudesDropdown from './common/SolicitudesDropdown';
import { useAuth } from '../contexts/AuthContext';
import { hasOpenAIChatsEnabled, isAdminColegio, isFeatureEnabled, normalizeRoleName } from '../utils/adminMenu';
import DataTable from './common/DataTable';
import solicitudesService from '../services/SolicitudesService';
import adminAsistenteService from '../services/AdminAsistenteService';
import LoadingSpinner from './common/LoadingSpinner';
import Alert from './common/Alert';
import UserSessionMenu from './common/UserSessionMenu';
import AdminMenu from './common/menus/AdminMenu';
import ParticularDashboardMenu from './common/menus/ParticularDashboardMenu';
import ProfessorMenu from './common/menus/ProfessorMenu';
import SearchableSelect from './common/SearchableSelect';
import brandLogo from '../assets/logos/logo_transparent_background.png';
import { isParticularChatGPTAllowed } from '../utils/particularChatGPTAccess';

const getSolicitudId = (item) => item?.id || item?.Id || '';

const getAssistantId = (item) => item?.idOpenAIAssistant || item?.IdOpenAIAssistant || '';

const getClassicPublicUrl = (item) =>
  item?.gptClasicoUrlPublica || item?.GPTClasicoURLPublica || item?.GptClasicoUrlPublica || '';

const getClassicName = (item) =>
  item?.gptClasicoNombre || item?.GPTClasicoNombre || item?.GptClasicoNombre || item?.nombre || item?.Nombre || 'Asistente IA';

const isOwnClassicAssistant = (item) => Boolean(item?.esPropio ?? item?.EsPropio ?? false);

const isFinalizedClassic = (item) => Boolean(item?.fechaFinalizacion || item?.FechaFinalizacion);

const isClassicCreationFinalized = (item) =>
  Boolean(item?.gptClasicoCreacionFinalizada ?? item?.GPTClasicoCreacionFinalizada ?? item?.fechaFinalizacion ?? item?.FechaFinalizacion);

const getDocenteId = (item) => item?.idDocente || item?.IdDocente || '';

const getDocenteFilterId = (item) => item?.idDocente || item?.IdDocente || item?.id || item?.Id || '';

const getDocenteName = (item) =>
  item?.nombreDocente || item?.NombreDocente || item?.usuarioNombre || item?.UsuarioNombre || 'Sin docente asociado';

const getDocenteEmail = (item) => item?.emailDocente || item?.EmailDocente || '';

const getDocenteAssistantTotal = (item) =>
  Number(item?.totalAsistentesDocente ?? item?.TotalAsistentesDocente ?? 0);

const getDocenteOwnAssistantTotal = (item) =>
  Number(item?.totalPropiosDocente ?? item?.TotalPropiosDocente ?? 0);

const getDocenteSharedAssistantTotal = (item) =>
  Number(item?.totalParaAlumnosDocente ?? item?.TotalParaAlumnosDocente ?? 0);

const normalizeClassicItem = (item, extra = {}) => {
  const activo = extra.activo ?? item?.activo ?? item?.Activo ?? true;
  return {
    ...item,
    ...extra,
    idOpenAIAssistant: extra.idOpenAIAssistant || getAssistantId(item) || '',
    esClasico: true,
    activo,
    Activo: activo
  };
};

const DEFAULT_ITEMS_PER_PAGE = 10;
const SHARE_CARD_WIDTH = 1400;
const SHARE_CARD_HEIGHT = 900;

const createInitialShareModalState = () => ({
  open: false,
  url: '',
  assistantName: '',
  schoolName: '',
  qrUrl: '',
  qrDataUrl: '',
  logoDataUrl: '',
  preparing: false,
  downloading: false,
  assetError: ''
});

const createInitialAdminResumenState = () => ({
  total: 0,
  totalPropios: 0,
  totalParaAlumnos: 0,
  topDocentes: []
});

const getClassicSchoolName = (item, fallback = 'Colegio') =>
  item?.nombreColegio ||
  item?.NombreColegio ||
  item?.colegioNombre ||
  item?.ColegioNombre ||
  fallback ||
  'Colegio';

const buildShareQrUrl = (url) => {
  if (!url) return '';
  return `https://quickchart.io/qr?text=${encodeURIComponent(url)}&size=360&margin=2&ecLevel=M`;
};

const escapeXml = (value = '') =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

const wrapText = (text, maxCharsPerLine = 28, maxLines = 2) => {
  const words = String(text || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!words.length) {
    return [''];
  }

  const lines = [];
  let currentLine = '';

  words.forEach((word) => {
    const candidate = currentLine ? `${currentLine} ${word}` : word;
    if (candidate.length <= maxCharsPerLine || !currentLine) {
      currentLine = candidate;
      return;
    }
    lines.push(currentLine);
    currentLine = word;
  });

  if (currentLine) {
    lines.push(currentLine);
  }

  if (lines.length <= maxLines) {
    return lines;
  }

  const visibleLines = lines.slice(0, maxLines);
  visibleLines[maxLines - 1] = `${visibleLines[maxLines - 1]} ${lines.slice(maxLines).join(' ')}`.trim();
  if (visibleLines[maxLines - 1].length > maxCharsPerLine) {
    visibleLines[maxLines - 1] = `${visibleLines[maxLines - 1].slice(0, maxCharsPerLine - 1).trimEnd()}…`;
  }

  return visibleLines;
};

const renderSvgTextLines = (lines, x, y, lineHeight, extraAttributes = '') => `
  <text x="${x}" y="${y}" ${extraAttributes}>
    ${lines
      .map((line, index) => `<tspan x="${x}"${index === 0 ? '' : ` dy="${lineHeight}"`}>${escapeXml(line)}</tspan>`)
      .join('')}
  </text>
`;

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('No pudimos leer el archivo generado.'));
    reader.readAsDataURL(blob);
  });

const fetchAssetAsDataUrl = async (assetUrl) => {
  const response = await fetch(assetUrl);
  if (!response.ok) {
    throw new Error('No pudimos preparar los recursos para compartir.');
  }
  return blobToDataUrl(await response.blob());
};

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('No pudimos renderizar la imagen para compartir.'));
    image.src = src;
  });

const downloadBlob = (blob, fileName) => {
  const downloadUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = downloadUrl;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.setTimeout(() => URL.revokeObjectURL(downloadUrl), 0);
};

const sanitizeFileName = (value = 'archivo') =>
  String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9-_]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase() || 'archivo';

const buildShareCardFileName = (schoolName, assistantName) =>
  `aluxia-${sanitizeFileName(schoolName)}-${sanitizeFileName(assistantName || 'gpt-clasico')}.png`;

const buildShareCardSvg = ({ assistantName, schoolName, qrDataUrl, logoDataUrl }) => {
  const assistantLines = wrapText(assistantName, 20, 3);
  const schoolLines = wrapText(schoolName, 26, 2);

  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="${SHARE_CARD_WIDTH}" height="${SHARE_CARD_HEIGHT}" viewBox="0 0 ${SHARE_CARD_WIDTH} ${SHARE_CARD_HEIGHT}">
      <defs>
        <linearGradient id="share-bg" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#0f172a" />
          <stop offset="52%" stop-color="#1d4ed8" />
          <stop offset="100%" stop-color="#0f766e" />
        </linearGradient>
        <linearGradient id="share-panel" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#111827" />
          <stop offset="100%" stop-color="#0f172a" />
        </linearGradient>
        <filter id="share-shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="18" stdDeviation="26" flood-color="#0f172a" flood-opacity="0.2" />
        </filter>
      </defs>

      <rect width="${SHARE_CARD_WIDTH}" height="${SHARE_CARD_HEIGHT}" fill="url(#share-bg)" />
      <circle cx="1240" cy="90" r="180" fill="#ffffff" fill-opacity="0.08" />
      <circle cx="112" cy="820" r="210" fill="#38bdf8" fill-opacity="0.16" />
      <circle cx="930" cy="800" r="120" fill="#22c55e" fill-opacity="0.12" />

      <rect x="80" y="84" width="1240" height="732" rx="36" fill="#ffffff" fill-opacity="0.12" />
      <rect x="100" y="104" width="1200" height="692" rx="34" fill="#ffffff" filter="url(#share-shadow)" />
      <rect x="120" y="124" width="700" height="652" rx="30" fill="url(#share-panel)" />
      <rect x="854" y="146" width="366" height="608" rx="30" fill="#f8fafc" filter="url(#share-shadow)" />

      <rect x="154" y="154" width="110" height="110" rx="28" fill="#ffffff" fill-opacity="0.14" />
      ${logoDataUrl ? `<image href="${logoDataUrl}" x="176" y="176" width="66" height="66" preserveAspectRatio="xMidYMid meet" />` : ''}

      <text x="290" y="195" font-family="Arial, Helvetica, sans-serif" font-size="22" font-weight="700" fill="#bfdbfe">aluxIA</text>
      <text x="290" y="232" font-family="Arial, Helvetica, sans-serif" font-size="18" font-weight="500" fill="#cbd5e1">Asistente IA compartible</text>

      <rect x="154" y="304" width="196" height="42" rx="21" fill="#ffffff" fill-opacity="0.12" stroke="#ffffff" stroke-opacity="0.22" />
      <text x="252" y="331" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="16" font-weight="600" fill="#f8fafc">Compartir acceso</text>

      <text x="154" y="402" font-family="Arial, Helvetica, sans-serif" font-size="52" font-weight="700" fill="#ffffff">Abrí este</text>
      <text x="154" y="462" font-family="Arial, Helvetica, sans-serif" font-size="52" font-weight="700" fill="#ffffff">asistente al instante</text>

      ${renderSvgTextLines(
        schoolLines,
        154,
        546,
        38,
        'font-family="Arial, Helvetica, sans-serif" font-size="28" font-weight="600" fill="#bfdbfe"'
      )}
      ${renderSvgTextLines(
        assistantLines,
        154,
        632,
        62,
        'font-family="Arial, Helvetica, sans-serif" font-size="48" font-weight="700" fill="#ffffff"'
      )}

      <rect x="906" y="196" width="262" height="262" rx="28" fill="#ffffff" />
      ${qrDataUrl ? `<image href="${qrDataUrl}" x="930" y="220" width="214" height="214" preserveAspectRatio="xMidYMid meet" />` : ''}
      <text x="1037" y="492" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="18" font-weight="700" fill="#0f172a">Escaneá y abrí el chat</text>
      <text x="1037" y="520" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="16" font-weight="500" fill="#475569">Compatible con celular, tablet y notebook</text>

      <text x="1037" y="724" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="14" font-weight="600" fill="#64748b">
        Generado desde aluxIA para compartir un asistente IA
      </text>
    </svg>
  `;
};

const getRange = (page, pageSize) => {
  const currentPage = Math.max(page || 1, 1);
  const take = Math.max(pageSize || DEFAULT_ITEMS_PER_PAGE, 1);
  const start = (currentPage - 1) * take;
  return { start, end: start + take - 1 };
};

const MyClassicGptAgents = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const featureFlags = useMemo(() => ColegioService.getFeatureFlags(), []);
  const activeColegio = useMemo(() => ColegioService.getActiveColegio(), []);
  const activeColegioId = useMemo(() => ColegioService.getActiveColegioId(), []);
  const activeParticular = useMemo(() => ParticularService.getActiveParticular(), []);
  const roleName = normalizeRoleName(user);
  const requestedScope = location.state?.scope || new URLSearchParams(location.search).get('scope');
  const isExplicitParticularScope = requestedScope === 'particulares' || requestedScope === 'particular';
  const activeColegioName = activeColegio?.nombre || activeColegio?.name || activeColegio?.descripcion || 'Colegio';
  const isAdmin = useMemo(() => roleName.includes('admin'), [roleName]);
  const isGeneralAdmin = useMemo(() => roleName.includes('admin') && !isAdminColegio(user), [roleName, user]);
  const isParticularContext = isExplicitParticularScope ||
    roleName.includes('particular') ||
    (isGeneralAdmin && Boolean(activeParticular));
  const handleChangeParticular = useCallback(() => {
    ParticularService.setActiveParticular(null);
    navigate('/admin/select-colegio', { replace: true, state: { scope: 'particulares' } });
  }, [navigate]);
  const hasOpenAIChats = hasOpenAIChatsEnabled(featureFlags);
  const hasModuloAlumnos = isParticularContext || isFeatureEnabled(featureFlags, 'ModuloAlumnos');
  const hasAgentesClasicos = isParticularContext
    ? isParticularChatGPTAllowed(activeParticular)
    : isFeatureEnabled(featureFlags, 'PermiteAgentesClasicosGPT');
  const [isLoadingPropios, setIsLoadingPropios] = useState(true);
  const [isLoadingPersonales, setIsLoadingPersonales] = useState(true);
  const [isLoadingMateriaOtros, setIsLoadingMateriaOtros] = useState(true);
  const [isLoadingAdminClasicos, setIsLoadingAdminClasicos] = useState(true);
  const [alert, setAlert] = useState({ show: false, message: '', type: 'error', title: '' });
  const [propios, setPropios] = useState([]);
  const [personales, setPersonales] = useState([]);
  const [materiaOtros, setMateriaOtros] = useState([]);
  const [adminClasicos, setAdminClasicos] = useState([]);
  const [adminDocentes, setAdminDocentes] = useState([]);
  const [adminResumenClasicos, setAdminResumenClasicos] = useState(createInitialAdminResumenState);
  const [adminSearch, setAdminSearch] = useState('');
  const [debouncedAdminSearch, setDebouncedAdminSearch] = useState('');
  const [selectedAdminDocenteIds, setSelectedAdminDocenteIds] = useState([]);
  const [paginacionPropios, setPaginacionPropios] = useState({
    paginaActual: 1,
    tamañoPagina: DEFAULT_ITEMS_PER_PAGE,
    total: 0
  });
  const [paginacionPersonales, setPaginacionPersonales] = useState({
    paginaActual: 1,
    tamañoPagina: DEFAULT_ITEMS_PER_PAGE,
    total: 0
  });
  const [paginacionMateriaOtros, setPaginacionMateriaOtros] = useState({
    paginaActual: 1,
    tamañoPagina: DEFAULT_ITEMS_PER_PAGE,
    total: 0
  });
  const [paginacionAdminClasicos, setPaginacionAdminClasicos] = useState({
    paginaActual: 1,
    tamañoPagina: DEFAULT_ITEMS_PER_PAGE,
    total: 0
  });
  const [docenteModal, setDocenteModal] = useState({
    open: false,
    docenteId: '',
    docenteNombre: '',
    docenteEmail: '',
    items: [],
    loading: false,
    error: '',
    pagination: {
      paginaActual: 1,
      tamañoPagina: DEFAULT_ITEMS_PER_PAGE,
      total: 0
    }
  });
  const [shareModal, setShareModal] = useState(createInitialShareModalState);
  const [asistenteToggleModal, setAsistenteToggleModal] = useState({ open: false, asistente: null, nextActivo: false, error: '' });
  const [asistenteToggleLoading, setAsistenteToggleLoading] = useState(false);
  const isLoading = isAdmin
    ? isLoadingAdminClasicos
    : isLoadingPropios || isLoadingPersonales || isLoadingMateriaOtros;
  const adminDocenteOptions = useMemo(() => {
    const seen = new Set();
    return (adminDocentes || [])
      .map((docente) => {
        const value = String(getDocenteFilterId(docente) || '');
        if (!value || seen.has(value)) return null;
        seen.add(value);
        const label = getDocenteName(docente);
        const secondary = getDocenteEmail(docente);
        const total = getDocenteAssistantTotal(docente);
        return {
          value,
          label,
          secondary: secondary ? `${secondary} · ${total} asistentes` : `${total} asistentes`,
          searchText: `${label} ${secondary || ''}`
        };
      })
      .filter(Boolean);
  }, [adminDocentes]);
  const adminTopDocentes = useMemo(
    () => Array.isArray(adminResumenClasicos.topDocentes) ? adminResumenClasicos.topDocentes : [],
    [adminResumenClasicos.topDocentes]
  );
  const hasAdminFilters = Boolean(adminSearch.trim() || selectedAdminDocenteIds.length > 0);

  const handleNavigate = (path, options = undefined) => {
    navigate(path, options);
  };

  const buildPublicUrl = (raw) => {
    if (!raw) return '';
    const trimmed = String(raw).trim();
    if (!trimmed) return '';
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      return trimmed;
    }
    return `${window.location.origin}/aula?asistente=${encodeURIComponent(trimmed)}`;
  };

  const copyTextToClipboard = async (value) => {
    if (!value) return;
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return;
    }

    const tempInput = document.createElement('input');
    tempInput.value = value;
    document.body.appendChild(tempInput);
    tempInput.select();
    document.execCommand('copy');
    document.body.removeChild(tempInput);
  };

  const handleShare = async (item) => {
    const publicUrl = buildPublicUrl(getClassicPublicUrl(item));
    if (!publicUrl) return;

    try {
      await copyTextToClipboard(publicUrl);
    } catch (error) {
      // El modal igualmente queda disponible para copiar manualmente.
    }

    setShareModal({
      ...createInitialShareModalState(),
      open: true,
      url: publicUrl,
      assistantName: getClassicName(item),
      schoolName: getClassicSchoolName(item, activeColegioName),
      qrUrl: buildShareQrUrl(publicUrl)
    });
  };

  const closeShareModal = () => {
    setShareModal(createInitialShareModalState());
  };

  const handleCopyShareLink = async () => {
    if (!shareModal.url) return;
    try {
      await copyTextToClipboard(shareModal.url);
      setAlert({
        show: true,
        title: 'Link copiado',
        message: 'La URL pública del asistente IA volvió a copiarse al portapapeles.',
        type: 'success'
      });
    } catch (error) {
      setAlert({
        show: true,
        title: 'No pudimos copiar el link',
        message: error.message || 'Copia el enlace manualmente desde el modal.',
        type: 'error'
      });
    }
  };

  const handleDownloadShareImage = async () => {
    if (!shareModal.url || !shareModal.qrUrl) return;

    setShareModal((prev) => ({
      ...prev,
      downloading: true,
      assetError: ''
    }));

    try {
      const [logoDataUrl, qrDataUrl] = await Promise.all([
        shareModal.logoDataUrl ? Promise.resolve(shareModal.logoDataUrl) : fetchAssetAsDataUrl(brandLogo),
        shareModal.qrDataUrl ? Promise.resolve(shareModal.qrDataUrl) : fetchAssetAsDataUrl(shareModal.qrUrl)
      ]);

      const svgMarkup = buildShareCardSvg({
        assistantName: shareModal.assistantName,
        schoolName: shareModal.schoolName,
        url: shareModal.url,
        qrDataUrl,
        logoDataUrl
      });

      const svgBlob = new Blob([svgMarkup], { type: 'image/svg+xml;charset=utf-8' });
      const svgUrl = URL.createObjectURL(svgBlob);

      try {
        const image = await loadImage(svgUrl);
        const canvas = document.createElement('canvas');
        canvas.width = SHARE_CARD_WIDTH;
        canvas.height = SHARE_CARD_HEIGHT;
        const context = canvas.getContext('2d');
        if (!context) {
          throw new Error('No pudimos inicializar la imagen para descarga.');
        }

        context.fillStyle = '#0f172a';
        context.fillRect(0, 0, SHARE_CARD_WIDTH, SHARE_CARD_HEIGHT);
        context.drawImage(image, 0, 0, SHARE_CARD_WIDTH, SHARE_CARD_HEIGHT);

        const pngBlob = await new Promise((resolve, reject) => {
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('No pudimos convertir la tarjeta a PNG.'));
                return;
              }
              resolve(blob);
            },
            'image/png',
            1
          );
        });

        downloadBlob(pngBlob, buildShareCardFileName(shareModal.schoolName, shareModal.assistantName));
      } finally {
        URL.revokeObjectURL(svgUrl);
      }

      setShareModal((prev) => ({
        ...prev,
        downloading: false,
        logoDataUrl,
        qrDataUrl
      }));
    } catch (error) {
      setShareModal((prev) => ({
        ...prev,
        downloading: false,
        assetError: error.message || 'No pudimos generar la imagen para compartir.'
      }));
    }
  };

  useEffect(() => {
    if (!shareModal.open || !shareModal.qrUrl || (shareModal.logoDataUrl && shareModal.qrDataUrl)) {
      return undefined;
    }

    let cancelled = false;

    const prepareAssets = async () => {
      setShareModal((prev) => (prev.open ? { ...prev, preparing: true } : prev));

      try {
        const [logoDataUrl, qrDataUrl] = await Promise.all([
          shareModal.logoDataUrl ? Promise.resolve(shareModal.logoDataUrl) : fetchAssetAsDataUrl(brandLogo),
          shareModal.qrDataUrl ? Promise.resolve(shareModal.qrDataUrl) : fetchAssetAsDataUrl(shareModal.qrUrl)
        ]);

        if (cancelled) return;

        setShareModal((prev) => (prev.open ? {
          ...prev,
          logoDataUrl,
          qrDataUrl,
          preparing: false
        } : prev));
      } catch (error) {
        if (cancelled) return;
        setShareModal((prev) => (prev.open ? { ...prev, preparing: false } : prev));
      }
    };

    prepareAssets();

    return () => {
      cancelled = true;
    };
  }, [shareModal.open, shareModal.qrUrl, shareModal.logoDataUrl, shareModal.qrDataUrl]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedAdminSearch(adminSearch.trim());
      setPaginacionAdminClasicos((prev) => (
        prev.paginaActual === 1
          ? prev
          : { ...prev, paginaActual: 1 }
      ));
    }, 350);

    return () => window.clearTimeout(timeoutId);
  }, [adminSearch]);

  const enrichAdminClassicItems = useCallback(async (items = []) => {
    if (!Array.isArray(items) || items.length === 0) return [];

    const detailEntries = await Promise.all(
      items.map(async (item) => {
        if (!isFinalizedClassic(item)) {
          return [getSolicitudId(item), {}];
        }

        const solicitudId = getSolicitudId(item);
        if (!solicitudId) {
          return ['', {}];
        }

        try {
          const response = await solicitudesService.getSolicitudesRecibidasAcciones(solicitudId, activeColegioId);
          return [
            solicitudId,
            {
              idOpenAIAssistant: response?.idOpenAIAssistant || response?.IdOpenAIAssistant || ''
            }
          ];
        } catch (error) {
          return [
            solicitudId,
            {
              accionesError: error.message || 'No pudimos obtener los datos del asistente IA.'
            }
          ];
        }
      })
    );

    const detailMap = new Map(detailEntries);
    return items.map((item) => normalizeClassicItem(item, detailMap.get(getSolicitudId(item)) || {}));
  }, [activeColegioId]);

  const handleAdminMenuNavigate = (item) => {
    if (!item) return;
    if (item.path) {
      handleNavigate(item.path, item.state ? { state: item.state } : undefined);
      return;
    }
    if (item.id === 'agentes-clasicos-gpt') {
      handleNavigate('/mis-agentes-clasicos-gpt');
      return;
    }
    handleNavigate('/admin-dashboard', { state: { tab: item.id } });
  };

  const handleClassicEdit = (item) => {
    const assistantId = getAssistantId(item);

    if (isAdmin) {
      if (!assistantId) {
        setAlert({
          show: true,
          title: 'Edición no disponible',
          message: 'Todavía no encontramos el asistente interno asociado a este asistente IA.',
          type: 'error'
        });
        return;
      }
      handleNavigate(`/admin/asistentes/${assistantId}/editar`);
      return;
    }

    if (!assistantId) {
      setAlert({
        show: true,
        title: 'Edición no disponible',
        message: 'No encontramos el asistente asociado a este asistente IA para generar la solicitud de edición.',
        type: 'error'
      });
      return;
    }

    handleNavigate(`/mis-agentes-clasicos-gpt/editar/${assistantId}`);
  };

  const handleViewReports = (item) => {
    const assistantId = getAssistantId(item);
    if (!assistantId) {
      setAlert({
        show: true,
        title: 'Reportes no disponibles',
        message: item?.accionesError || 'No encontramos el asistente interno asociado a este asistente IA.',
        type: 'error'
      });
      return;
    }

    handleNavigate('/reports', {
      state: {
        ...(isParticularContext ? { scope: 'particulares' } : {}),
        assistant: {
          ...item,
          idOpenAIAssistant: assistantId,
          nombreAsistente: getClassicName(item),
          esClasico: true
        }
      }
    });
  };

  const handleOpenAsistenteToggle = (item) => {
    const assistantId = getAssistantId(item);
    if (!assistantId) {
      setAlert({
        show: true,
        title: 'Cambio de estado no disponible',
        message: item?.accionesError || 'No encontramos el asistente interno asociado a este asistente IA.',
        type: 'error'
      });
      return;
    }

    const activo = item?.activo ?? item?.Activo ?? true;
    setAsistenteToggleModal({ open: true, asistente: item, nextActivo: !activo, error: '' });
  };

  const handleConfirmToggleAsistente = async () => {
    const asistente = asistenteToggleModal.asistente;
    const assistantId = getAssistantId(asistente);

    if (!assistantId) {
      setAsistenteToggleModal((prev) => ({
        ...prev,
        error: 'No encontramos el identificador del asistente.'
      }));
      return;
    }

    setAsistenteToggleLoading(true);
    try {
      const response = await adminAsistenteService.updateAsistenteActivo(assistantId, asistenteToggleModal.nextActivo);
      const nuevoEstado = response?.activo ?? response?.Activo ?? asistenteToggleModal.nextActivo;
      const syncState = (items) =>
        (items || []).map((item) =>
          getAssistantId(item) === assistantId
            ? { ...item, activo: nuevoEstado, Activo: nuevoEstado }
            : item
        );

      setPropios((prev) => syncState(prev));
      setPersonales((prev) => syncState(prev));
      setMateriaOtros((prev) => syncState(prev));
      setAdminClasicos((prev) => syncState(prev));
      setDocenteModal((prev) => ({
        ...prev,
        items: syncState(prev.items)
      }));
      setAsistenteToggleModal({ open: false, asistente: null, nextActivo: false, error: '' });
      setAlert({
        show: true,
        title: 'Estado actualizado',
        message: `El asistente IA "${getClassicName(asistente)}" fue ${nuevoEstado ? 'habilitado' : 'deshabilitado'}.`,
        type: 'success'
      });
    } catch (error) {
      setAsistenteToggleModal((prev) => ({
        ...prev,
        error: error.message || 'No pudimos cambiar el estado del asistente IA.'
      }));
    } finally {
      setAsistenteToggleLoading(false);
    }
  };

  const loadAdminClasicos = useCallback(async (pagina = paginacionAdminClasicos.paginaActual, tamañoPagina = paginacionAdminClasicos.tamañoPagina) => {
    if (!isAdmin || !hasAgentesClasicos) {
      setAdminClasicos([]);
      setAdminDocentes([]);
      setAdminResumenClasicos(createInitialAdminResumenState());
      setPaginacionAdminClasicos((prev) => ({ ...prev, total: 0, paginaActual: 1 }));
      setIsLoadingAdminClasicos(false);
      return;
    }

    setIsLoadingAdminClasicos(true);
    setAlert({ show: false, message: '', type: 'error', title: '' });

    try {
      const { start, end } = getRange(pagina, tamañoPagina);
      const response = await adminAsistenteService.getAsistentesClasicosColegio({
        start,
        end,
        search: debouncedAdminSearch,
        docenteIds: selectedAdminDocenteIds
      });
      const items = response?.items || [];
      const normalizados = items.map((item) => normalizeClassicItem(item));
      const docentes = response?.docentes || [];
      const resumen = response?.resumen || createInitialAdminResumenState();

      setAdminClasicos(normalizados);
      setAdminDocentes(docentes);
      setAdminResumenClasicos({
        total: Number(resumen.total ?? response?.total ?? normalizados.length),
        totalPropios: Number(resumen.totalPropios ?? 0),
        totalParaAlumnos: Number(resumen.totalParaAlumnos ?? 0),
        topDocentes: Array.isArray(resumen.topDocentes) ? resumen.topDocentes : []
      });
      setPaginacionAdminClasicos((prev) => ({
        ...prev,
        paginaActual: pagina,
        tamañoPagina,
        total: response?.total ?? normalizados.length
      }));
    } catch (error) {
      setAlert({
        show: true,
        title: 'Error al cargar asistentes IA',
        message: error.message || 'No pudimos cargar los asistentes IA del colegio.',
        type: 'error'
      });
      setAdminClasicos([]);
      setAdminDocentes([]);
      setAdminResumenClasicos(createInitialAdminResumenState());
      setPaginacionAdminClasicos((prev) => ({ ...prev, total: 0 }));
    } finally {
      setIsLoadingAdminClasicos(false);
    }
  }, [
    debouncedAdminSearch,
    hasAgentesClasicos,
    isAdmin,
    paginacionAdminClasicos.paginaActual,
    paginacionAdminClasicos.tamañoPagina,
    selectedAdminDocenteIds
  ]);

  const loadDocenteClasicos = useCallback(async (
    docenteId,
    pagina = 1,
    tamañoPagina = DEFAULT_ITEMS_PER_PAGE
  ) => {
    if (!docenteId) return;

    setDocenteModal((prev) => ({
      ...prev,
      loading: true,
      error: ''
    }));

    try {
      const { start, end } = getRange(pagina, tamañoPagina);
      const response = await adminAsistenteService.getAsistentesClasicosColegio({ start, end, docenteId });
      const items = (response?.items || []).map((item) => normalizeClassicItem(item));

      setDocenteModal((prev) => ({
        ...prev,
        items,
        loading: false,
        pagination: {
          paginaActual: pagina,
          tamañoPagina,
          total: response?.total ?? items.length
        }
      }));
    } catch (error) {
      setDocenteModal((prev) => ({
        ...prev,
        items: [],
        loading: false,
        error: error.message || 'No pudimos cargar los asistentes IA del docente.',
        pagination: {
          ...prev.pagination,
          total: 0
        }
      }));
    }
  }, []);

  const handleOpenDocenteModal = (item) => {
    const docenteId = getDocenteId(item);
    if (!docenteId) {
      setAlert({
        show: true,
        title: 'Docente no disponible',
        message: 'No encontramos un docente asociado a este asistente IA.',
        type: 'error'
      });
      return;
    }

    setDocenteModal({
      open: true,
      docenteId,
      docenteNombre: getDocenteName(item),
      docenteEmail: getDocenteEmail(item),
      items: [],
      loading: true,
      error: '',
      pagination: {
        paginaActual: 1,
        tamañoPagina: DEFAULT_ITEMS_PER_PAGE,
        total: 0
      }
    });
    loadDocenteClasicos(docenteId, 1, DEFAULT_ITEMS_PER_PAGE);
  };

  const closeDocenteModal = () => {
    setDocenteModal((prev) => ({
      ...prev,
      open: false,
      items: [],
      loading: false,
      error: ''
    }));
  };

  const loadSolicitudesPropias = useCallback(async (pagina = paginacionPropios.paginaActual, tamañoPagina = paginacionPropios.tamañoPagina) => {
    if (!hasAgentesClasicos) {
      setPropios([]);
      setPaginacionPropios((prev) => ({ ...prev, total: 0, paginaActual: 1 }));
      setIsLoadingPropios(false);
      return;
    }

    setIsLoadingPropios(true);
    setAlert({ show: false, message: '', type: 'error', title: '' });

    try {
      const { start, end } = getRange(pagina, tamañoPagina);
      const response = await solicitudesService.getSolicitudesGptClasicos({
        start,
        end,
        scope: isAdmin ? 'todos' : 'propios',
        esPropio: false
      });
      const items = response?.items || response?.Items || [];

      const normalizados = Array.isArray(items)
        ? (isAdmin ? await enrichAdminClassicItems(items) : items.map((item) => normalizeClassicItem(item)))
        : [];

      setPropios(normalizados);
      setPaginacionPropios((prev) => ({
        ...prev,
        paginaActual: pagina,
        tamañoPagina,
        total: response?.total ?? normalizados.length
      }));
    } catch (error) {
      setAlert({
        show: true,
        title: 'Error al cargar solicitudes',
        message: error.message || 'No pudimos cargar las solicitudes de asistentes IA.',
        type: 'error'
      });
      setPropios([]);
      setPaginacionPropios((prev) => ({ ...prev, total: 0 }));
    } finally {
      setIsLoadingPropios(false);
    }
  }, [enrichAdminClassicItems, hasAgentesClasicos, isAdmin, paginacionPropios.paginaActual, paginacionPropios.tamañoPagina]);

  const loadSolicitudesPersonales = useCallback(async (pagina = paginacionPersonales.paginaActual, tamañoPagina = paginacionPersonales.tamañoPagina) => {
    if (!hasAgentesClasicos) {
      setPersonales([]);
      setPaginacionPersonales((prev) => ({ ...prev, total: 0, paginaActual: 1 }));
      setIsLoadingPersonales(false);
      return;
    }

    setIsLoadingPersonales(true);

    try {
      const { start, end } = getRange(pagina, tamañoPagina);
      const response = await solicitudesService.getSolicitudesGptClasicos({
        start,
        end,
        scope: isAdmin ? 'todos' : 'propios',
        esPropio: true
      });
      const items = response?.items || response?.Items || [];

      const normalizados = Array.isArray(items)
        ? (isAdmin ? await enrichAdminClassicItems(items) : items.map((item) => normalizeClassicItem(item)))
        : [];

      setPersonales(normalizados);
      setPaginacionPersonales((prev) => ({
        ...prev,
        paginaActual: pagina,
        tamañoPagina,
        total: response?.total ?? normalizados.length
      }));
    } catch (error) {
      setAlert({
        show: true,
        title: 'Error al cargar asistentes propios',
        message: error.message || 'No pudimos cargar los asistentes propios.',
        type: 'error'
      });
      setPersonales([]);
      setPaginacionPersonales((prev) => ({ ...prev, total: 0 }));
    } finally {
      setIsLoadingPersonales(false);
    }
  }, [enrichAdminClassicItems, hasAgentesClasicos, isAdmin, paginacionPersonales.paginaActual, paginacionPersonales.tamañoPagina]);

  const loadSolicitudesMateriaOtros = useCallback(async (pagina = paginacionMateriaOtros.paginaActual, tamañoPagina = paginacionMateriaOtros.tamañoPagina) => {
    if (isAdmin || !hasAgentesClasicos) {
      setMateriaOtros([]);
      setPaginacionMateriaOtros((prev) => ({ ...prev, total: 0, paginaActual: 1 }));
      setIsLoadingMateriaOtros(false);
      return;
    }

    setIsLoadingMateriaOtros(true);

    try {
      const { start, end } = getRange(pagina, tamañoPagina);
      const response = await solicitudesService.getSolicitudesGptClasicos({
        start,
        end,
        scope: 'materiaOtros'
      });
      const items = response?.items || response?.Items || [];
      const normalizados = Array.isArray(items)
        ? items.map((item) => normalizeClassicItem(item))
        : [];

      setMateriaOtros(normalizados);
      setPaginacionMateriaOtros((prev) => ({
        ...prev,
        paginaActual: pagina,
        tamañoPagina,
        total: response?.total ?? normalizados.length
      }));
    } catch (error) {
      setAlert({
        show: true,
        title: 'Error al cargar solicitudes',
        message: error.message || 'No pudimos cargar los asistentes IA de la materia.',
        type: 'error'
      });
      setMateriaOtros([]);
      setPaginacionMateriaOtros((prev) => ({ ...prev, total: 0 }));
    } finally {
      setIsLoadingMateriaOtros(false);
    }
  }, [hasAgentesClasicos, isAdmin, paginacionMateriaOtros.paginaActual, paginacionMateriaOtros.tamañoPagina]);

  useEffect(() => {
    if (!isAdmin) {
      setAdminClasicos([]);
      setAdminDocentes([]);
      setAdminResumenClasicos(createInitialAdminResumenState());
      setIsLoadingAdminClasicos(false);
      return;
    }

    loadAdminClasicos(paginacionAdminClasicos.paginaActual, paginacionAdminClasicos.tamañoPagina);
  }, [isAdmin, loadAdminClasicos, paginacionAdminClasicos.paginaActual, paginacionAdminClasicos.tamañoPagina]);

  useEffect(() => {
    if (isAdmin) {
      setPropios([]);
      setIsLoadingPropios(false);
      return;
    }

    loadSolicitudesPropias(paginacionPropios.paginaActual, paginacionPropios.tamañoPagina);
  }, [isAdmin, loadSolicitudesPropias, paginacionPropios.paginaActual, paginacionPropios.tamañoPagina]);

  useEffect(() => {
    if (isAdmin) {
      setPersonales([]);
      setIsLoadingPersonales(false);
      return;
    }

    loadSolicitudesPersonales(paginacionPersonales.paginaActual, paginacionPersonales.tamañoPagina);
  }, [isAdmin, loadSolicitudesPersonales, paginacionPersonales.paginaActual, paginacionPersonales.tamañoPagina]);

  useEffect(() => {
    if (isAdmin) {
      setMateriaOtros([]);
      setIsLoadingMateriaOtros(false);
      return;
    }

    loadSolicitudesMateriaOtros(paginacionMateriaOtros.paginaActual, paginacionMateriaOtros.tamañoPagina);
  }, [isAdmin, loadSolicitudesMateriaOtros, paginacionMateriaOtros.paginaActual, paginacionMateriaOtros.tamañoPagina]);

  const handlePropiosPageChange = (pagina) => {
    setPaginacionPropios((prev) => ({ ...prev, paginaActual: pagina }));
  };

  const handlePropiosItemsPerPageChange = (tamañoPagina) => {
    setPaginacionPropios((prev) => ({
      ...prev,
      paginaActual: 1,
      tamañoPagina
    }));
  };

  const handlePersonalesPageChange = (pagina) => {
    setPaginacionPersonales((prev) => ({ ...prev, paginaActual: pagina }));
  };

  const handlePersonalesItemsPerPageChange = (tamañoPagina) => {
    setPaginacionPersonales((prev) => ({
      ...prev,
      paginaActual: 1,
      tamañoPagina
    }));
  };

  const handleMateriaOtrosPageChange = (pagina) => {
    setPaginacionMateriaOtros((prev) => ({ ...prev, paginaActual: pagina }));
  };

  const handleMateriaOtrosItemsPerPageChange = (tamañoPagina) => {
    setPaginacionMateriaOtros((prev) => ({
      ...prev,
      paginaActual: 1,
      tamañoPagina
    }));
  };

  const handleAdminClasicosPageChange = (pagina) => {
    setPaginacionAdminClasicos((prev) => ({ ...prev, paginaActual: pagina }));
  };

  const handleAdminClasicosItemsPerPageChange = (tamañoPagina) => {
    setPaginacionAdminClasicos((prev) => ({
      ...prev,
      paginaActual: 1,
      tamañoPagina
    }));
  };

  const handleAdminDocentesFilterChange = (nextValues) => {
    setSelectedAdminDocenteIds(Array.isArray(nextValues) ? nextValues : []);
    setPaginacionAdminClasicos((prev) => ({
      ...prev,
      paginaActual: 1
    }));
  };

  const handleClearAdminFilters = () => {
    setAdminSearch('');
    setDebouncedAdminSearch('');
    setSelectedAdminDocenteIds([]);
    setPaginacionAdminClasicos((prev) => ({
      ...prev,
      paginaActual: 1
    }));
  };

  const handleDocenteModalPageChange = (pagina) => {
    const docenteId = docenteModal.docenteId;
    if (!docenteId) return;
    loadDocenteClasicos(docenteId, pagina, docenteModal.pagination.tamañoPagina);
  };

  const handleDocenteModalItemsPerPageChange = (tamañoPagina) => {
    const docenteId = docenteModal.docenteId;
    if (!docenteId) return;
    loadDocenteClasicos(docenteId, 1, tamañoPagina);
  };

  const buildColumns = (allowEdit = true, allowShare = true) => [
    {
      key: 'gptClasicoNombre',
      title: 'GPT',
      sortable: true,
      width: 'lg',
      render: (value, item) => {
        const isFinalizada = isFinalizedClassic(item);
        const hasError = Boolean(item?.error || item?.Error);
        const statusLabel = hasError ? 'Con error' : isFinalizada ? 'Finalizada' : 'Pendiente';
        const statusClass = hasError ? 'error' : isFinalizada ? 'active' : 'pending';
        const nombre = value || getClassicName(item);
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
            <span>{nombre}</span>
            <span
              className={`status-badge ${statusClass}`}
              style={{ fontSize: '0.65rem', padding: '0.2rem 0.6rem', alignSelf: 'flex-start' }}
            >
              {statusLabel}
            </span>
          </div>
        );
      }
    },
    { key: 'nombreMateria', title: 'Materia', sortable: true, width: 'md' },
    { key: 'nombreTipoSolicitud', title: 'Tipo', sortable: true, width: 'md' },
    { key: 'gptClasicoModelo', title: 'Modelo', sortable: true, width: 'md' },
    {
      key: 'fechaCreacion',
      title: 'Creación',
      sortable: true,
      width: 'md',
      render: (value, item) => {
        const raw = item?.fechaCreacion || item?.FechaCreacion;
        if (!raw) return '-';
        const date = new Date(raw);
        return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString();
      }
    },
    {
      key: 'acciones',
      title: 'Acciones',
      sortable: false,
      width: 'md',
      render: (value, item) => {
        const isFinalizada = isFinalizedClassic(item);
        if (!isFinalizada) return '';
        const publicUrl = getClassicPublicUrl(item);
        const assistantId = getAssistantId(item);
        const activo = item?.activo ?? item?.Activo ?? true;
        return (
          <div className="table-actions">
            {allowEdit && (
              <button
                type="button"
                className="table-btn table-btn-edit"
                title="Editar"
                onClick={() => handleClassicEdit(item)}
                disabled={!assistantId}
              >
                <i className="fas fa-pen"></i>
              </button>
            )}
            <button
              type="button"
              className="table-btn table-btn-test"
              title="Probar"
              onClick={() => {
                const link = buildPublicUrl(publicUrl);
                if (link) {
                  window.open(link, '_blank', 'noopener,noreferrer');
                }
              }}
              disabled={!publicUrl}
            >
              <i className="fas fa-play"></i>
            </button>
            {allowShare && (
              <button
                type="button"
                className="table-btn table-btn-reports"
                title="Compartir"
                onClick={() => handleShare(item)}
                disabled={!publicUrl || (!isAdmin && isOwnClassicAssistant(item))}
              >
                <i className="fas fa-share-alt"></i>
              </button>
            )}
            {isAdmin && (
              <>
                <button
                  type="button"
                  className="table-btn table-btn-reports"
                  title="Ver reportes"
                  onClick={() => handleViewReports(item)}
                  disabled={!assistantId}
                >
                  <i className="fas fa-chart-bar"></i>
                </button>
                <button
                  type="button"
                  className={`table-btn ${activo ? 'table-btn-toggle-inactive' : 'table-btn-toggle-active'}`}
                  title={activo ? 'Deshabilitar asistente IA' : 'Habilitar asistente IA'}
                  onClick={() => handleOpenAsistenteToggle(item)}
                  disabled={!assistantId}
                >
                  <i className={`fas ${activo ? 'fa-toggle-on' : 'fa-toggle-off'}`}></i>
                </button>
              </>
            )}
          </div>
        );
      }
    }
  ];

  const buildAdminColumns = (context = 'main') => [
    {
      key: 'gptClasicoNombre',
      title: 'GPT',
      sortable: true,
      width: 'lg',
      render: (value, item) => {
        const nombre = value || getClassicName(item);
        const descripcion = item?.gptClasicoDescripcion || item?.GPTClasicoDescripcion || item?.descripcion || item?.Descripcion;
        return (
          <div className="classic-agent-main-cell">
            <span className="classic-agent-name">{nombre}</span>
            {descripcion && <span className="classic-agent-muted">{descripcion}</span>}
          </div>
        );
      }
    },
    {
      key: 'nombreDocente',
      title: 'Docente',
      sortable: true,
      width: 'lg',
      render: (value, item) => (
        <div className="classic-agent-main-cell">
          <span className="classic-agent-name">{value || getDocenteName(item)}</span>
          {getDocenteEmail(item) && <span className="classic-agent-muted">{getDocenteEmail(item)}</span>}
        </div>
      )
    },
    {
      key: 'totalAsistentesDocente',
      title: 'Asistentes docente',
      sortable: true,
      width: 'md',
      render: (value, item) => {
        const total = Number(value ?? getDocenteAssistantTotal(item));
        const docenteId = getDocenteId(item);
        return (
          <button
            type="button"
            className="docente-count-button"
            onClick={() => handleOpenDocenteModal(item)}
            disabled={!docenteId}
            title={docenteId ? 'Ver asistentes del docente' : 'Docente no disponible'}
          >
            <span>{total}</span>
            <i className="fas fa-list"></i>
          </button>
        );
      }
    },
    { key: 'nombreMateria', title: 'Materia', sortable: true, width: 'md' },
    { key: 'gptClasicoModelo', title: 'Modelo', sortable: true, width: 'md' },
    {
      key: 'esPropio',
      title: 'Uso',
      sortable: true,
      width: 'md',
      render: (value, item) => {
        const esPropio = Boolean(value ?? item?.EsPropio ?? false);
        return (
          <span className={`classic-agent-use-badge ${esPropio ? 'own' : 'shared'}`}>
            <i className={`fas ${esPropio ? 'fa-user-lock' : 'fa-user-graduate'}`}></i>
            {esPropio ? 'Propio' : 'Para alumnos'}
          </span>
        );
      }
    },
    {
      key: 'activo',
      title: 'Estado',
      sortable: true,
      width: 'md',
      render: (value, item) => {
        const activo = value ?? item?.Activo ?? true;
        const finalizado = isClassicCreationFinalized(item);
        return (
          <div className="classic-agent-state-cell">
            <span className={`status-badge ${activo ? 'active' : 'inactive'}`}>
              {activo ? 'Habilitado' : 'Deshabilitado'}
            </span>
            {!finalizado && (
              <span className="status-badge pending">
                Pendiente
              </span>
            )}
          </div>
        );
      }
    },
    {
      key: 'fechaAlta',
      title: 'Creación',
      sortable: true,
      width: 'md',
      render: (value, item) => {
        const raw = value || item?.FechaAlta;
        if (!raw) return '-';
        const date = new Date(raw);
        return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString();
      }
    },
    {
      key: 'acciones',
      title: 'Acciones',
      sortable: false,
      width: 'md',
      render: (value, item) => {
        const publicUrl = getClassicPublicUrl(item);
        const assistantId = getAssistantId(item);
        const activo = item?.activo ?? item?.Activo ?? true;
        return (
          <div className="table-actions">
            <button
              type="button"
              className="table-btn table-btn-edit"
              title="Editar"
              onClick={() => handleClassicEdit(item)}
              disabled={!assistantId}
            >
              <i className="fas fa-pen"></i>
            </button>
            <button
              type="button"
              className="table-btn table-btn-test"
              title="Probar"
              onClick={() => {
                const link = buildPublicUrl(publicUrl);
                if (link) {
                  window.open(link, '_blank', 'noopener,noreferrer');
                }
              }}
              disabled={!publicUrl}
            >
              <i className="fas fa-play"></i>
            </button>
            <button
              type="button"
              className="table-btn table-btn-reports"
              title="Compartir"
              onClick={() => handleShare(item)}
              disabled={!publicUrl}
            >
              <i className="fas fa-share-alt"></i>
            </button>
            <button
              type="button"
              className="table-btn table-btn-reports"
              title="Ver reportes"
              onClick={() => handleViewReports(item)}
              disabled={!assistantId}
            >
              <i className="fas fa-chart-bar"></i>
            </button>
            <button
              type="button"
              className={`table-btn ${activo ? 'table-btn-toggle-inactive' : 'table-btn-toggle-active'}`}
              title={activo ? 'Deshabilitar asistente IA' : 'Habilitar asistente IA'}
              onClick={() => handleOpenAsistenteToggle(item)}
              disabled={!assistantId}
            >
              <i className={`fas ${activo ? 'fa-toggle-on' : 'fa-toggle-off'}`}></i>
            </button>
          </div>
        );
      }
    }
  ].filter((column) => context === 'main' || column.key !== 'totalAsistentesDocente');

  const columnasPropios = buildColumns(true, true);
  const columnasPersonales = buildColumns(true, false);
  const columnasMateriaOtros = buildColumns(false, true);
  const columnasAdminClasicos = buildAdminColumns('main');
  const columnasDocenteModal = buildAdminColumns('modal');

  return (
    <div className={`new-dashboard my-assistants classic-gpt-agents-page ${isParticularContext ? 'pd-pro' : ''}`}>
      <aside className="dashboard-sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-graduation-cap"></i>
            <span>aluxIA</span>
          </div>
        </div>

        {isParticularContext ? (
          <ParticularDashboardMenu
            activeId="asistentes"
            onNavigate={(item) => item?.path && navigate(item.path, item.state ? { state: item.state } : undefined)}
            externalNavigation
            messageScope="particular"
          />
        ) : isAdmin ? (
          <AdminMenu
            activeId="agentes-clasicos-gpt"
            user={user}
            featureFlags={featureFlags}
            onNavigate={handleAdminMenuNavigate}
          />
        ) : (
          <ProfessorMenu
            activeId="agentes-clasicos-gpt"
            hasOpenAIChats={hasOpenAIChats}
            hasAgentesClasicos={hasAgentesClasicos}
            hasModuloAlumnos={hasModuloAlumnos}
            onNavigate={(item) => item?.path && handleNavigate(item.path)}
          />
        )}

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className="fas fa-user"></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Usuario'}</span>
              <span className="user-role">{user?.roleName || user?.nombreRol || user?.rol || user?.role || (isAdmin ? 'Admin' : 'Profesor')}</span>
            </div>
          </div>
        </div>
      </aside>

      <main className="dashboard-main">
        <header className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">
              <i className="fas fa-robot"></i>
              {isAdmin ? 'Asistentes IA' : 'Mis Asistentes IA'}
            </h1>
            <p className="page-subtitle">
              {isAdmin
                ? 'Administra, prueba, reporta y deshabilita los asistentes IA del colegio.'
                : 'Administra tus asistentes IA'}
            </p>
          </div>

          <div className="header-right">
            <SolicitudesDropdown />
            {!isParticularContext && <ActiveColegioBadge />}
            <UserSessionMenu
              user={user}
              onLogout={logout}
              passwordLabel="Actualizar Password"
              extraItems={isParticularContext && isGeneralAdmin ? [{
                key: 'switch-particular',
                label: 'Cambiar particular',
                icon: 'fas fa-exchange-alt',
                onClick: handleChangeParticular
              }] : []}
            />
          </div>
        </header>

        <div className="dashboard-content">
          {alert.show && (
            <Alert
              title={alert.title}
              message={alert.message}
              type={alert.type}
              onClose={() => setAlert((prev) => ({ ...prev, show: false }))}
            />
          )}

          {!hasAgentesClasicos ? (
            <div className="content-card">
              <div className="card-content">
                <div className="empty-state">
                  <div className="empty-state-content">
                    <i className="fas fa-ban empty-state-icon"></i>
                    <h3>Asistentes IA deshabilitados</h3>
                    <p>La funcionalidad de asistentes IA está apagada para este colegio.</p>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => handleNavigate(isAdmin ? '/admin-dashboard' : '/dashboard')}
                    >
                      Volver
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (isLoading && (isAdmin ? adminClasicos.length === 0 : propios.length === 0 && personales.length === 0 && materiaOtros.length === 0)) ? (
            <div className="loading-container">
              <LoadingSpinner message="Cargando asistentes IA..." />
            </div>
          ) : (
            <>
              <div className="assistant-cta" style={{ marginBottom: '1.5rem' }}>
                <div className="cta-text">
                  <h3>Crea un nuevo asistente IA</h3>
                  <p>
                    {isAdmin
                      ? 'Inicia la configuración de un nuevo asistente IA para el colegio.'
                      : 'Envía una solicitud para que un administrador lo configure.'}
                  </p>
                </div>
                <button
                  className="btn btn-primary"
                  onClick={() => handleNavigate('/mis-agentes-clasicos-gpt/nuevo')}
                >
                  <i className="fas fa-plus"></i>
                  Nuevo asistente IA
                </button>
              </div>

              {isAdmin ? (
                <div className="content-card classic-agents-card">
                  <div className="card-header">
                    <h2 className="card-title">
                      <i className="fas fa-users"></i>
                      Asistentes IA del colegio
                    </h2>
                    <span className="classic-agent-card-count">
                      {paginacionAdminClasicos.total} asistentes
                    </span>
                  </div>
                  <div className="card-content">
                    <div className="classic-agent-filter-bar">
                      <div className="classic-agent-filter-field classic-agent-filter-search">
                        <label>Buscar</label>
                        <div className="classic-agent-search-input">
                          <i className="fas fa-search" aria-hidden="true"></i>
                          <input
                            type="text"
                            value={adminSearch}
                            onChange={(event) => setAdminSearch(event.target.value)}
                            placeholder="Buscar por asistente, materia, modelo o docente"
                            autoComplete="off"
                          />
                        </div>
                      </div>
                      <div className="classic-agent-filter-field classic-agent-filter-docentes">
                        <label>Docentes</label>
                        <SearchableSelect
                          value={selectedAdminDocenteIds}
                          values={selectedAdminDocenteIds}
                          onChange={handleAdminDocentesFilterChange}
                          options={adminDocenteOptions}
                          placeholder="Buscar y seleccionar docentes"
                          emptyText="No hay docentes disponibles en este colegio."
                          noResultsText="No encontramos docentes con esa búsqueda."
                          multiple
                        />
                      </div>
                      {hasAdminFilters && (
                        <button
                          type="button"
                          className="classic-agent-clear-filters"
                          onClick={handleClearAdminFilters}
                        >
                          <i className="fas fa-times"></i>
                          Limpiar
                        </button>
                      )}
                    </div>
                    <div className="classic-agent-insights">
                      <div className="classic-agent-summary-grid">
                        <div className="classic-agent-summary-item own">
                          <span className="classic-agent-summary-icon">
                            <i className="fas fa-user-lock"></i>
                          </span>
                          <span className="classic-agent-summary-copy">
                            <span className="classic-agent-summary-label">Propios</span>
                            <strong>{adminResumenClasicos.totalPropios}</strong>
                          </span>
                        </div>
                        <div className="classic-agent-summary-item shared">
                          <span className="classic-agent-summary-icon">
                            <i className="fas fa-user-graduate"></i>
                          </span>
                          <span className="classic-agent-summary-copy">
                            <span className="classic-agent-summary-label">Para alumnos</span>
                            <strong>{adminResumenClasicos.totalParaAlumnos}</strong>
                          </span>
                        </div>
                      </div>
                      <div className="classic-agent-top-docentes">
                        <div className="classic-agent-top-header">
                          <span>
                            <i className="fas fa-trophy"></i>
                            Top 10 docentes
                          </span>
                        </div>
                        {adminTopDocentes.length > 0 ? (
                          <div className="classic-agent-top-table" role="table" aria-label="Top 10 docentes con más asistentes">
                            <div className="classic-agent-top-row header" role="row">
                              <span role="columnheader">Docente</span>
                              <span role="columnheader">Total</span>
                              <span role="columnheader">Propios</span>
                              <span role="columnheader">Alumnos</span>
                            </div>
                            <div className="classic-agent-top-body">
                              {adminTopDocentes.map((docente, index) => (
                                <div
                                  key={getDocenteFilterId(docente) || `${getDocenteName(docente)}-${index}`}
                                  className="classic-agent-top-row"
                                  role="row"
                                >
                                  <span className="classic-agent-top-info" role="cell">
                                    <span className="classic-agent-top-name">{getDocenteName(docente)}</span>
                                    {getDocenteEmail(docente) && (
                                      <span className="classic-agent-top-email">{getDocenteEmail(docente)}</span>
                                    )}
                                  </span>
                                  <span className="classic-agent-top-count total" role="cell">
                                    {getDocenteAssistantTotal(docente)}
                                  </span>
                                  <span className="classic-agent-top-count own" role="cell">
                                    {getDocenteOwnAssistantTotal(docente)}
                                  </span>
                                  <span className="classic-agent-top-count shared" role="cell">
                                    {getDocenteSharedAssistantTotal(docente)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="classic-agent-top-empty">
                            Sin asistentes asignados todavía.
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="classic-agent-table-scroll">
                      <DataTable
                        data={adminClasicos}
                        columns={columnasAdminClasicos}
                        loading={isLoadingAdminClasicos}
                        emptyMessage="No hay asistentes IA cargados para este colegio."
                        externalPagination={true}
                        externalTotalItems={paginacionAdminClasicos.total}
                        externalTotalPages={Math.max(1, Math.ceil(paginacionAdminClasicos.total / paginacionAdminClasicos.tamañoPagina))}
                        externalCurrentPage={paginacionAdminClasicos.paginaActual}
                        onPageChange={handleAdminClasicosPageChange}
                        onItemsPerPageChange={handleAdminClasicosItemsPerPageChange}
                        defaultItemsPerPage={paginacionAdminClasicos.tamañoPagina}
                        exportFileName="agentes-clasicos-gpt-colegio"
                        exportTitle="Asistentes IA del colegio"
                        className="admin-classic-agents-table"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="content-card" style={{ marginBottom: '1.5rem' }}>
                    <div className="card-header">
                      <h2 className="card-title">
                        <i className="fas fa-user"></i>
                        Mis asistentes IA compartibles
                      </h2>
                    </div>
                    <div className="card-content">
                      <DataTable
                        data={propios}
                        columns={columnasPropios}
                        loading={isLoadingPropios}
                        emptyMessage="No tienes asistentes IA compartibles."
                        externalPagination={true}
                        externalTotalItems={paginacionPropios.total}
                        externalTotalPages={Math.max(1, Math.ceil(paginacionPropios.total / paginacionPropios.tamañoPagina))}
                        externalCurrentPage={paginacionPropios.paginaActual}
                        onPageChange={handlePropiosPageChange}
                        onItemsPerPageChange={handlePropiosItemsPerPageChange}
                        defaultItemsPerPage={paginacionPropios.tamañoPagina}
                      />
                    </div>
                  </div>

                  <div className="content-card" style={{ marginBottom: '1.5rem' }}>
                    <div className="card-header">
                      <h2 className="card-title">
                        <i className="fas fa-user-lock"></i>
                        Asistentes propios
                      </h2>
                    </div>
                    <div className="card-content">
                      <DataTable
                        data={personales}
                        columns={columnasPersonales}
                        loading={isLoadingPersonales}
                        emptyMessage="No hay asistentes propios cargados."
                        externalPagination={true}
                        externalTotalItems={paginacionPersonales.total}
                        externalTotalPages={Math.max(1, Math.ceil(paginacionPersonales.total / paginacionPersonales.tamañoPagina))}
                        externalCurrentPage={paginacionPersonales.paginaActual}
                        onPageChange={handlePersonalesPageChange}
                        onItemsPerPageChange={handlePersonalesItemsPerPageChange}
                        defaultItemsPerPage={paginacionPersonales.tamañoPagina}
                      />
                    </div>
                  </div>

                  <div className="content-card">
                    <div className="card-header">
                      <h2 className="card-title">
                        <i className="fas fa-users"></i>
                        Asistentes Clasicos GPT de tu materia (otros docentes)
                      </h2>
                    </div>
                    <div className="card-content">
                      <DataTable
                        data={materiaOtros}
                        columns={columnasMateriaOtros}
                        loading={isLoadingMateriaOtros}
                        emptyMessage="No hay solicitudes de otros usuarios para tus materias."
                        externalPagination={true}
                        externalTotalItems={paginacionMateriaOtros.total}
                        externalTotalPages={Math.max(1, Math.ceil(paginacionMateriaOtros.total / paginacionMateriaOtros.tamañoPagina))}
                        externalCurrentPage={paginacionMateriaOtros.paginaActual}
                        onPageChange={handleMateriaOtrosPageChange}
                        onItemsPerPageChange={handleMateriaOtrosItemsPerPageChange}
                        defaultItemsPerPage={paginacionMateriaOtros.tamañoPagina}
                      />
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
        {docenteModal.open && (
          <div className="modal-overlay" onClick={closeDocenteModal}>
            <div className="modal-content docente-classic-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <div>
                  <h3 className="modal-title">
                    <i className="fas fa-chalkboard-teacher"></i>
                    Asistentes IA del docente
                  </h3>
                  <p className="docente-classic-modal-subtitle">
                    {docenteModal.docenteNombre}
                    {docenteModal.docenteEmail ? ` · ${docenteModal.docenteEmail}` : ''}
                  </p>
                </div>
                <button type="button" className="modal-close-btn" onClick={closeDocenteModal}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="modal-body docente-classic-modal-body">
                {docenteModal.error && (
                  <Alert
                    title="Error al cargar asistentes"
                    message={docenteModal.error}
                    type="error"
                    onClose={() => setDocenteModal((prev) => ({ ...prev, error: '' }))}
                  />
                )}
                <div className="classic-agent-table-scroll">
                  <DataTable
                    data={docenteModal.items}
                    columns={columnasDocenteModal}
                    loading={docenteModal.loading}
                    emptyMessage="Este docente no tiene asistentes IA cargados."
                    externalPagination={true}
                    externalTotalItems={docenteModal.pagination.total}
                    externalTotalPages={Math.max(1, Math.ceil(docenteModal.pagination.total / docenteModal.pagination.tamañoPagina))}
                    externalCurrentPage={docenteModal.pagination.paginaActual}
                    onPageChange={handleDocenteModalPageChange}
                    onItemsPerPageChange={handleDocenteModalItemsPerPageChange}
                    defaultItemsPerPage={docenteModal.pagination.tamañoPagina}
                    exportFileName={`agentes-clasicos-${sanitizeFileName(docenteModal.docenteNombre || 'docente')}`}
                    exportTitle={`Asistentes IA - ${docenteModal.docenteNombre || 'Docente'}`}
                    className="docente-classic-agents-table"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeDocenteModal}>
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}
        {shareModal.open && (
          <div className="modal-overlay" onClick={closeShareModal}>
            <div className="modal-content share-assistant-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className="modal-title">
                  <i className="fas fa-share-alt"></i>
                  Compartir asistente IA
                </h3>
                <button type="button" className="modal-close-btn" onClick={closeShareModal}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="modal-body share-assistant-body">
                <div className="share-assistant-layout share-assistant-layout-single">
                  <div className="share-assistant-preview share-assistant-preview-full">
                    <div className="share-assistant-preview-stack">
                      <div className="share-assistant-copy-badge share-assistant-copy-badge-centered">
                        <i className="fas fa-check-circle"></i>
                        El link ya quedó copiado al portapapeles
                      </div>

                      <div className="share-assistant-card">
                        <div className="share-assistant-card-brand">
                          <div className="share-assistant-card-logo">
                            <img src={brandLogo} alt="aluxIA" />
                          </div>
                          <div>
                            <span className="share-assistant-card-app">aluxIA</span>
                            <span className="share-assistant-card-tag">Asistente IA</span>
                          </div>
                        </div>

                        <div className="share-assistant-card-copy">
                          <span className="share-assistant-card-copy-label">Colegio</span>
                          <h4>{shareModal.schoolName || activeColegioName}</h4>
                          <p>{shareModal.assistantName || 'Asistente IA'}</p>
                        </div>

                        <div className="share-assistant-card-qr-shell">
                          <div className="share-assistant-card-qr">
                            {shareModal.qrUrl ? (
                              <img
                                src={shareModal.qrUrl}
                                alt={`QR para ${shareModal.assistantName || 'Asistente IA'}`}
                              />
                            ) : (
                              <div className="share-assistant-card-qr-placeholder">
                                <i className="fas fa-qrcode"></i>
                              </div>
                            )}
                          </div>
                          <div className="share-assistant-card-qr-text">
                            <strong>Escaneá y abrí el asistente</strong>
                            <span>Funciona desde celular, tablet o notebook.</span>
                          </div>
                        </div>

                        <div className="share-assistant-card-link">
                          <span>Link directo</span>
                          <p>{shareModal.url}</p>
                        </div>

                        <div className="share-assistant-card-footer">
                          {shareModal.preparing && (
                            <span className="share-assistant-card-status">
                              <i className="fas fa-spinner fa-spin"></i>
                              Preparando imagen...
                            </span>
                          )}
                        </div>
                      </div>

                      {shareModal.assetError && (
                        <div className="share-assistant-error">
                          <i className="fas fa-exclamation-triangle"></i>
                          <span>{shareModal.assetError}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeShareModal}>
                  Cerrar
                </button>
                <button type="button" className="btn btn-secondary" onClick={handleCopyShareLink}>
                  <i className="fas fa-copy"></i>
                  Copiar link
                </button>
                <a
                  className="btn btn-secondary"
                  href={shareModal.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fas fa-external-link-alt"></i>
                  Abrir link
                </a>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleDownloadShareImage}
                  disabled={shareModal.downloading}
                >
                  {shareModal.downloading ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      Generando imagen...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-download"></i>
                      Descargar imagen
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
        {asistenteToggleModal.open && (
          <div className="modal-overlay" onClick={() => {
            if (!asistenteToggleLoading) {
              setAsistenteToggleModal({ open: false, asistente: null, nextActivo: false, error: '' });
            }
          }}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className="modal-title">
                  <i className="fas fa-power-off"></i>
                  Cambiar estado del asistente IA
                </h3>
                <button
                  type="button"
                  className="modal-close-btn"
                  onClick={() => setAsistenteToggleModal({ open: false, asistente: null, nextActivo: false, error: '' })}
                  disabled={asistenteToggleLoading}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="modal-body">
                <p>
                  {asistenteToggleModal.nextActivo
                    ? `Se habilitará el asistente IA "${getClassicName(asistenteToggleModal.asistente)}".`
                    : `Se deshabilitará el asistente IA "${getClassicName(asistenteToggleModal.asistente)}" y dejará de estar disponible.`}
                </p>
                {asistenteToggleModal.error && (
                  <p style={{ color: '#b91c1c' }}>{asistenteToggleModal.error}</p>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setAsistenteToggleModal({ open: false, asistente: null, nextActivo: false, error: '' })}
                  disabled={asistenteToggleLoading}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleConfirmToggleAsistente}
                  disabled={asistenteToggleLoading}
                >
                  {asistenteToggleLoading ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      Procesando...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-check"></i>
                      Confirmar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default MyClassicGptAgents;
